import { ISchemaValidationError, ISchemaValidationResult, ISchemaValidationWarning, SchemaValidationErrorType, SchemaValidationWarningType } from "Global/Base/Utils/ObjectSchemaValidator";
import { EventsController } from "Global/Dom/EventsController";
import { AttrValueReader } from "OW.Remoting/Remoting/AttrValueReader";
import { Checkbox } from "UI/UI.BaseControls/Checkbox";
import { GenericControl } from "UI/UI.BaseControls/GenericControl";
import { UIUtils } from "../../../../../Global/Dom/UIUtils";
import { Localization } from "../../../../../OW.Remoting/Remoting/Localization";
import { Control } from "../../../../UI.BaseControls/Control";
import { IBooleanDetailValueEvent } from "../../../../UI.BaseControls/IEvent";
import { Action } from "../../../../UI.Controls/Actions/Action";
import { DropDownMenuHorizontalOpen } from "../../../../UI.Controls/DropDownMenu/DropDownInterfaces";
import { DropDownMenu } from "../../../../UI.Controls/DropDownMenu/DropDownMenu";
import { DropDownMenuItem } from "../../../../UI.Controls/DropDownMenu/DropDownMenuItem";
import { DropDownPromptItem } from "../../../../UI.Controls/DropDownMenu/DropDownPromptItem";
import { SeparatorMenuItem } from "../../../../UI.Controls/DropDownMenu/SeparatorItem";
import { createElement, registerCustomElement } from "../../../../UI.ElementarControls/Utils/CustomElementUtils";
import { FilterValidationMessageBuilder } from "../../DataBuilders/FilterValidationMessageBuilder";
import { LocalizedStringBuilder } from "../../DataBuilders/LocalizedStringBuilder";
import { MessageButtonType, MessageType, MessageWindow } from "../../MessageWindow";
import { IForm } from "../IForm";
import { DropDownMenuCheckboxItem } from "./DropDownMenuItems/DropDownMenuCheckboxItem";
import { DropDownMenuButtonDisplayOn, DropDownMenuItemWithButton, IDropDownMenuButton } from "./DropDownMenuItems/DropDownMenuItemWithButton";
import { FilterBlock } from "./FilterBlock";
import { FilterCombo } from "./FilterCombo";
import { FilterDatetypeOperationMapper } from "./FilterDatetypeOperationMapper";
import { FilterManager } from "./FilterManager";
import { FilterValidator } from "./FilterValidator";

export class FilterPanel extends Control {
    //#region Events
    //#endregion Events

    //#region Fields
    /** Tlačítko filtrovat */
    private _filterButton: HTMLElement;
    /** Kontejner pro tlačítko filtru */
    private _topContainer: HTMLElement;
    /** Kontejner pro tlačítka AND/OR */
    private _conditionElement: HTMLElement;
    /** Tlačítko AND */
    private _andBtnElement: HTMLElement;
    /** Tlačítko OR */
    private _orBtnElement: HTMLElement;
    private _filterMenuEmptyBtn: DropDownMenuItem;
    private _headerPanel: HTMLElement;
    /** Vrchní řádek headeru obsahující název filtru a křížek pro zavření panelu */
    private _headerTopRowContainer: HTMLDivElement;
    /** Název filtru v panelu */
    private _headerText: HTMLSpanElement;
    /** Ikona která spustí dropdown */
    private _headerDropdownIcon: HTMLButtonElement;
    /** Tlačítka s akcemi */
    private _headerRowActionButtons: HTMLDivElement;
    private _filterManager: FilterManager;
    /** Element pro vložení filtru */
    private _filterInsertElement: HTMLElement;
    /** Textarea pro vložení filtru */
    private _filterInsertTextarea: HTMLTextAreaElement;
    /** Combobox pro výběr sloupců ke třídění */
    private _filterCombo: FilterCombo;
    /** Menu umožňující manipulovat s filtry aktuálního frame */
    private _filterMenu: DropDownMenu;
    /** Obsah panelu */
    private _contentFilter: HTMLElement;
    private _color: string = "color-default";
    private _headerImplementationCheckbox: Checkbox;
    private _isNameEditing: boolean = false;

    private _headerRowButtonsInitiated: boolean = false;
    private _headerRowButtonsCreated: boolean = false;

    //#region Fields Row Buttons
    private _headerTurnOffButton: HTMLButtonElement;

    private _headerSaveAsButton: HTMLButtonElement;

    private _headerSaveButton: HTMLButtonElement;

    private _headerRevertChangesButton: HTMLButtonElement;

    private _headerDeleteButton: HTMLButtonElement;

    private _nameEditingInput: HTMLInputElement;

    private _nameEditingIconsRow: HTMLDivElement;

    private _createHeaderRowButtonsPromise?: Promise<boolean> = null;

    //#endregion Fields Row Buttons

    private _events: EventsController;

    //#endregion Fields

    //#region Properties
    public ownerForm: IForm;

    public set color(value: string) {
        if (!value) {
            this._headerPanel.classList.remove(this._color);
            this._color = "color-default";
        } else {
            if (this._color && this._color.length > 0)
                this._headerPanel.classList.remove(this._color);

            this._color = value;
        }

        this._headerPanel.classList.add(this._color);
    }

    public get color(): string {
        return this._color;
    }

    /** Vrací aktuálně zobrazený FilterBlock nebo null */
    public get currentFilterBlock(): FilterBlock {
        if (this._contentFilter.lastChild instanceof FilterBlock) {
            return this._contentFilter.lastChild;
        }

        return null;
    }

    /** Vrací, zda je viditelný vstup pro vložení dříve zkopírovaného filtru */
    protected get _isInsertFilterVisible(): boolean {
        return !!(this._filterInsertElement && this._filterInsertElement.parentElement);
    }
    //#endregion Properties

    //#region Listeners
    //#endregion Listeners

    //#region HtmlElement
    protected override _constructor(): void {
        super._constructor();
        this._events = new EventsController();
        this._createFilterMenu();
        this._createHeaderFilterPanel();
        this._createHeaderFilterWrapper();
        this.appendChild(this._headerPanel);
        this.appendChild(this._contentFilter);
        this.hide();
    }
    //#endregion HtmlElement

    //#region Methods
    /** Nastaví pro panel aktuálně používaný FilterManager
     * @returns true, pokud se filterManager změnil
    */
    public async updateFilterManager(manager: FilterManager): Promise<boolean> {
        this._removeInsertFilter(); // Vstup má být odstraněn vždy při aktualizaci managera (tzn. například spuštění filtru)

        const filterManagerChanged = this._filterManager !== manager;

        this._filterManager = manager;

        // Mohlo dojít k uložení jako přes dropdown
        if (this._filterManager?.getCurrentFilter()?.filterName) {
            if (this._isNameEditing) {
                this._cancelNameEditing();
            }

            await this._updateStateOfButtons();
            this._createImplementationFilterCheckbox();
            this._changeHeaderFilterName(this._filterManager.getCurrentFilter()?.filterName);
        } else if (this._filterManager) {
            Localization.addLocData(this._headerText, "GUI-289646"); // GUI-289646 - Filtr
            await this._updateStateOfButtons();
        }

        if (!filterManagerChanged) {
            return false;
        }

        this._filterCombo.clearItems();
        this._createFilterManagerEventListeners();
        this._registerFilterButtonAction();

        if (this._filterMenu.isVisible()) {
            await this.loadFilterItems();
        }

        return true;
    }

    /**
     * Přidá sloupec do comba s výběrem sloupců
     * @param locKey Lokalizační klíč
     * @param action Akce, která se má spustit po kliknutí na položku
     */
    public addColumnItem(locKey: string, action: () => Promise<void>): void {
        this._filterCombo.addItem(locKey, action);
    }

    /**
     * Vloží do panelu požadovaný blok s filtry
     * @param block Požadovaný blok
     */
    public showFilterBlock(block: FilterBlock): void {
        this._contentFilter.appendChild(block as HTMLElement);
    }

    /** Zaregistruje akci na stisk tlačítka pro filtrování */
    protected _registerFilterButtonAction(): void {
        this._events.addListener(this._filterButton, "click", () => {
            if (this._filterButton.classList.contains("Disabled"))
                return;
            if (this._isInsertFilterVisible) {
                this._parseInsertedFilter(this._filterInsertTextarea.value);
            }

            if (this._filterManager.currentFilter) {
                this._filterManager.currentFilter.execute();
            } else {
                this._filterManager.clearFilter();
            }
        });
    }

    /**
     * Zaregistruje akci, která se spustí po změně AND/OR
     * @param action Akce
     */
    public registerSignButtonAction(action: (sign: string) => void): void {
        this._events.addListener(this._andBtnElement, "click", () => {
            if (this._andBtnElement.classList.contains("Disabled"))
                return;
            this.updateOrAndButtonsState(false);

            action("and");
        });

        this._events.addListener(this._orBtnElement, "click", () => {
            if (this._orBtnElement.classList.contains("Disabled"))
                return;
            this.updateOrAndButtonsState(true);

            action("or");
        });
    }

    /**
     * Aktualizuje aktivnost filtrovacího tlačítka
     * @param disabled Požadovaný stav
     */
    public updateFilterButtonState(disabled: boolean): void {
        (this._filterButton as HTMLButtonElement).disabled = disabled;
    }

    /**
     * Aktualizuje aktivnost AND/OD tlačítek
     * @param activateOr Požadovaný stav
     */
    public updateOrAndButtonsState(activateOr: boolean): void {
        this._orBtnElement.classList.toggle("active", activateOr);
        this._andBtnElement.classList.toggle("active", !activateOr);
    }

    /**
     * Aktualizuje viditelnost bloku s tlačítky AND/OR
     * @param visible Zda má být viditelný
     */
    public updateConditionElementState(visible: boolean): void {
        if (visible) {
            this._conditionElement.style.display = "";
        } else {
            this._conditionElement.style.display = "none";
        }
    }

    /**
     * Zobrazí vstup pro vložení dříve zkopírovaného filtru
     * @param filter Hodnota, která se má zobrazit ve vstupu
     */
    public showInsertFilter(filter?: string): void {
        if (!this._filterInsertElement) {
            this._filterInsertElement = createElement({ classes: "filter-insert FilterBlock" });
            const container = createElement({ classes: "FilterGroup" });
            this._filterInsertTextarea = createElement({ type: "textarea", classes: "Edit" }) as HTMLTextAreaElement;

            this._events.addListener(this._filterInsertTextarea, "keypress", (event: KeyboardEvent) => {
                if (event.key === "Enter") { // enter, potvrzeni
                    event.preventDefault();

                    if (!this._parseInsertedFilter(this._filterInsertTextarea.value))
                        return;

                    this._filterManager.execute();
                }
            });

            this._events.addListener(this._filterInsertTextarea, "input", () => {
                if (this._filterInsertTextarea.value)
                    this.updateFilterButtonState(false);
            });

            container.appendChild(this._filterInsertTextarea);
            this.updateFilterButtonState(false);

            if ((navigator as any).clipboard && (navigator as any).clipboard.readText != null) {
                const insertButton = createElement({ type: "div", classes: "btn primary" });
                Localization.addLocData(insertButton, "GUI-673317"); // Vložit obsah schránky
                GenericControl.registerEventListener(insertButton, "click", async (_e: ClipboardEvent) => {
                    if (insertButton.classList.contains("Disabled"))
                        return;
                    const clipboardText = await (navigator as any).clipboard.readText();
                    this._filterInsertTextarea.focus();
                    this._filterInsertTextarea.value = clipboardText;
                });
                container.appendChild(insertButton);
            }
            this._filterInsertElement.appendChild(container);
        }

        this._filterInsertTextarea.value = (filter) ? filter : "";

        if (!this._isInsertFilterVisible) {
            this._contentFilter.insertBefore(this._filterInsertElement, this._conditionElement);
        }
    }

    /**
     * Zpracuje vstup z vkládání zkopírovaného filtru
     * @param text Vstup
     */
    protected _parseInsertedFilter(text?: string): boolean {
        const userJson = text;
        if (userJson && userJson.trim() !== "") {
            const userJsonString = AttrValueReader.read(userJson);
            let filterDataObject;
            try {
                filterDataObject = JSON.parse(userJsonString);
            } catch (e) {
                MessageWindow.showAlert(new LocalizedStringBuilder("GUI-674763", [e]), "GUI-674376"); //Nelze aplikovat filtr. Nepodařilo se parsovat JSON. @0 | Vložit filtr
                return false;
            }

            // Kontrola, jestli existují všechny sloupce v zadaném filtru
            if (!FilterValidator.validateColumnDataTypes(filterDataObject, this._filterManager.attrNameToDataType)) {
                return false;
            }
            // Kontrola validnosti JSONu
            if (!this._processValidationResults(FilterValidator.validateFilterData(filterDataObject))) {
                return false;
            }

            const userFilter = FilterDatetypeOperationMapper.transformUserJson(userJsonString, this._filterManager.attrNameToDataType);
            const insertedFilter = this._filterManager.createNew(false);
            insertedFilter.attrNameToDataType = this._filterManager.attrNameToDataType;
            insertedFilter.loadData(userFilter);
            insertedFilter.unsave();
            this._filterManager.selectFilter(insertedFilter);
            this._filterManager.updateFilterPanel();

            this._removeInsertFilter();
        }

        return true;
    }

    private _createHeaderFilterPanel(): void {
        this.classList.add("FilterPanel");

        // vytvoření headeru
        this._headerPanel = createElement({ type: "div", classes: ["header", "color-default"] }) as HTMLDivElement;

        // Vytvoření tlačítka s názvem filtru
        const headerFilterName = createElement({ type: "div", classes: ["headerFilterName"] }) as HTMLDivElement;
        this._headerText = createElement({ type: "span", classes: ["text", "title"] }) as HTMLSpanElement;

        // Lokalizace hlavičky filtru
        Localization.addLocData(this._headerText, "GUI-289646"); // GUI-289646 - Filtr

        // Vytvoření ikony pro spuštění dropdownu
        this._headerDropdownIcon = createElement({ type: "button", classes: ["btn", "flat", "icon", "ui-more"] }) as HTMLButtonElement;

        // Přidá k panelu text a ikonu
        headerFilterName.appendChild(this._headerText);
        headerFilterName.appendChild(this._headerDropdownIcon);

        // Přidání listeneru pro zobrazení dropdownu
        this._events.addListener(this._headerDropdownIcon, "click", async (e) => {
            if (this._filterMenu.isOpened) {
                this._filterMenu.close();
            } else {
                if (!this._isNameEditing) {
                    this._headerDropdownIcon.disabled = true;
                    await this.loadFilterItems(); // Může dojít ke změně ve filtermenu na ListFrame
                    this._filterMenu.show(headerFilterName, e);
                    this._headerDropdownIcon.disabled = false;
                }
            }
        });

        this._events.addListener(this._headerText, "click", () => {
            this._promptHeaderEditing();
        });

        // Křížek na zavření panelu
        const closeFilterPanelButton = createElement({ type: "button", classes: ["btn", "flat", "icon", "ui-cross-small", "close"] });
        this._events.addListener(closeFilterPanelButton, "click", () => {
            this.hide();
        });

        Localization.addLocDescData(closeFilterPanelButton, "GUI-668877", "title"); // Zavřít panel

        // Řádek obsahující křížek a jméno filtru
        this._headerTopRowContainer = createElement({ type: "div", classes: ["headerTopRowContainer"] }) as HTMLDivElement;
        this._headerTopRowContainer.appendChild(headerFilterName);
        this._headerTopRowContainer.appendChild(closeFilterPanelButton);
        this._headerPanel.appendChild(this._headerTopRowContainer);

        // Vytvoření řádku pro tlačítka akcí
        this._headerRowActionButtons = createElement({ type: "div", classes: ["headerRowActionButtons"] }) as HTMLDivElement;
    }

    private _createHeaderFilterWrapper(): void {
        this._contentFilter = createElement({ classes: "FilterWrapper" });
        this._topContainer = createElement({ type: "div", classes: ["FilterTopContainer"] });

        // Vytvoření tlačítka pro přidaní dalšího sloupce k aktuálnímu filtru
        this._filterCombo = new FilterCombo();
        this._filterCombo.defaultLocKey = "GUI-311870"; // Přidat sloupec
        this._filterCombo.classList.add("AddColumn");
        this._topContainer.appendChild(this._filterCombo as HTMLElement);

        // Vytvoření tlačítka pro spuštění aktuálního filtru
        this._filterButton = createElement({ type: "button", classes: ["btn-filter", "btn", "primary"], value: "Filtrovat" });
        Localization.addLocData(this._filterButton, "GUI-280088"); // Filtrovat
        this._topContainer.appendChild(this._filterButton);
        this._contentFilter.appendChild(this._topContainer);

        // Vytvoření přepínače podmínek u filtru
        this._conditionElement = createElement({ classes: "condition" });
        this._andBtnElement = createElement({ classes: ["btn-condition", "and", "active"] });
        this._orBtnElement = createElement({ classes: ["btn-condition", "or"] });

        // Vytvoření přepínače podmínek pro více filtračních skupin
        const innerSpanAnd = createElement({ type: "span" });
        Localization.addLocData(innerSpanAnd, "GUI-666143"); // Vše současně
        this._andBtnElement.appendChild(innerSpanAnd);
        const innerSpanOr = createElement({ type: "span" });
        Localization.addLocData(innerSpanOr, "GUI-666144"); // Alespoň jeden
        this._orBtnElement.appendChild(innerSpanOr);

        this._conditionElement.style.display = "none";

        this._conditionElement.appendChild(this._andBtnElement);
        this._conditionElement.appendChild(this._orBtnElement);
        this._contentFilter.appendChild(this._conditionElement);
    }

    private _createFilterManagerEventListeners(): void {
        const saveStateListener = async (_sender: FilterManager, filterBlock: FilterBlock): Promise<void> => {
            const filterName = this._filterManager.getCurrentFilter()?.filterName;
            if (filterName && filterName !== this._headerText.innerText) {
                this._changeHeaderFilterName(filterBlock.filterName);
            }

            if (filterBlock && filterBlock.filterId !== this._filterManager.getCurrentFilter()?.filterId) {
                return;
            }

            if (!this._isNameEditing) {
                await this._updateStateOfButtons();
                this._createImplementationFilterCheckbox();
            }
        };

        const filterClearedListener = async () => {
            await this._resetPanel();
            await this._resetHeaderName();
        };

        // Po změně stavu filtru se panel aktualizuje
        this._events.add(this._filterManager.filterSaveStateChanged, saveStateListener);
        // Při smazání filtru se vyresetuje panel
        this._events.add(this._filterManager.filterCleared, filterClearedListener);

        document.addEventListener("filterShowImplementationCheckbox", () => {
            this._createImplementationFilterCheckbox();
        });
    }
    /**
     * Zpracovává výstup z validace vloženého filtru a zobrazuje informace uživateli
     * @param validationResults Výsledky validace
     */
    private _processValidationResults(validationResults: ISchemaValidationResult<object>[]): boolean {
        let success = true;
        const errors: ISchemaValidationError<object>[] = [];
        const warnings: ISchemaValidationWarning[] = [];
        const errorStrings: string[] = [];
        const warningsStrings: string[] = [];

        for (const validationResult of validationResults) {
            if (validationResult.success === false)
                success = false;

            validationResult.errors.forEach((validationError) => {
                const errorMessage = `Field '<b>${validationError.field.name}</b>' with value = '${validationError.value}' ended up with error '${SchemaValidationErrorType[validationError.errorType]}'.`; // `
                //resultText += errorMessage + '\n';
                errors.push(validationError);
                errorStrings.push(errorMessage);
            });

            validationResult.warnings.forEach((validationWaring) => {
                const warningMessage = `Field '${validationWaring.fieldName && validationWaring.fieldName.toString()}' with value = '${validationWaring.value}' ended up with warning '${SchemaValidationWarningType[validationWaring.warningType]}'.`; // `
                //resultText += warningMessage + '\n';
                warnings.push(validationWaring);
                warningsStrings.push(warningMessage);
            });
        }

        if ((errors.length > 0) || (warnings.length > 0)) {
            const validationMessageBuilder = new FilterValidationMessageBuilder(errors, warnings);
            MessageWindow.showAlert(validationMessageBuilder, "GUI-674343"); // Chyby a varování struktury filtru
        }

        return success;
    }

    /** Odstraní vstup pro vložení dříve zkopírovaného filtru */
    protected _removeInsertFilter(): void {
        if (this._isInsertFilterVisible) {
            this._contentFilter.removeChild(this._filterInsertElement);
        }
    }

    /** Vytvoří menu pro správu filtru */
    private _createFilterMenu(): void {
        // vytvořené menu se nesmí smazat, jinak se uživateli do již otevřeného menu nepromítnou nové položky
        if (this._filterMenu == null) {
            this._filterMenu = new DropDownMenu();
            this._filterMenu.horizontalOpenPosition = DropDownMenuHorizontalOpen.left;
        } else {
            this._filterMenu.clearItems();
        }

        this._filterMenu.addItem(FilterPanel._createDropMenuItem("new", async () => {
            this._filterManager.createNew();
            this._filterManager.updateFilterPanel();
            this._headerText.innerText = await Localization.getTextPromise("GUI-289646"); //Filtr
            await this._updateStateOfButtons();
            this._createImplementationFilterCheckbox();
        }, "GUI-382590")); // Nový

        this._filterMenu.addItem(FilterPanel._createDropMenuItem("insert", async () => {
            this._filterManager.insertFilter();
        }, "GUI-674376")); // Vložit filtr

        this._filterMenu.addItem(FilterPanel._createDropMenuItem("filter", async () => {
            this._filterManager.showAdvancedFilter();
        }, "GUI-691373")); // Pokročilé filtrování

        this._filterMenu.addItem(new SeparatorMenuItem());
        this._filterMenuEmptyBtn = FilterPanel._createDropMenuItem("checked",
            async () => {
                if (!(await this.ownerForm.checkAndSaveForm({ cancelEditIfNotEdited: true }))) {
                    return;
                }
                this._filterManager.clearFilter();
                this._filterMenuEmptyBtn.icon = "checked";
                await this._resetPanel();
                await this._resetHeaderName();
            },
            "GUI-670216"); //Vše
        this._filterMenu.addItem(this._filterMenuEmptyBtn); // Nefiltrovat
    }

    /**
     * Načte seznam filtrů do menu pro filtry
     */
    public async loadFilterItems(): Promise<void> {
        if (!this._headerRowButtonsInitiated) {
            await this._createHeaderRowButtons();
        } else if (this._createHeaderRowButtonsPromise) {
            await this._createHeaderRowButtonsPromise;
        }

        this._createFilterMenu();

        if (!this._filterManager) {
            return;
        }

        const info = await this._filterManager.getFilterList();
        const dropDownItemsPromiseArr: Promise<DropDownMenuItem>[] = [];

        for (const [id, filterBlock] of info) {
            dropDownItemsPromiseArr.push(this._createNewFilterItem(id, filterBlock));
        }

        const resolvedItemArray = await Promise.all(dropDownItemsPromiseArr);

        for (const dropDownItem of resolvedItemArray) {
            this._filterMenu.addItem(dropDownItem);
        }
    }

    private _createImplementationFilterCheckbox(): void {
        if (this._filterManager?.canEditImplementationFilters) {
            if (!this._headerImplementationCheckbox) {
                this._headerImplementationCheckbox = new Checkbox();
                this._headerImplementationCheckbox.isNullable = false;
                this._headerImplementationCheckbox.classList.add("headerImplementationCheckbox");
                Localization.addLocData(this._headerImplementationCheckbox, "GUI-457012", "text");

                this._events.addListener(this._headerImplementationCheckbox, "change", () => {
                    this._filterManager.getCurrentFilter().isImplementationFilter = this._headerImplementationCheckbox.value;
                    this._filterManager.getCurrentFilter().unsave();
                });
            }

            this._headerImplementationCheckbox.visible = true;
            this._headerImplementationCheckbox.value = this._filterManager.getCurrentFilter()?.isImplementationFilter;
            this._headerPanel.append(this._headerImplementationCheckbox);
        }
    }

    //#region Tvorba buttonu

    /** Vrací informaci, zda uživatel může měnit uložený filtr
     * @param filterBlock
     * @param filterManager
     */
    private static _canEditFilter(filterBlock: FilterBlock, filterManager: FilterManager): boolean {
        return !filterBlock.isImplementationFilter || filterManager.canEditImplementationFilters;
    }

    /**
     * Tlačítka uloženého nezměněného filtru
     * @param filterBlock
     * @param filterManager
     */
    private static _getSavedButton(filterBlock: FilterBlock, filterManager: FilterManager): IDropDownMenuButton[] {
        const buttons: IDropDownMenuButton[] = [];
        if (this._canEditFilter(filterBlock, filterManager))
            buttons.push({
                locKey: "GUI-668860", //Přejmenovat filtr
                displayOn: DropDownMenuButtonDisplayOn.hover,
                action: async (item) => {
                    const implementationCheckbox = filterManager.canEditImplementationFilters ? new DropDownMenuCheckboxItem() : null;
                    if (implementationCheckbox) {
                        implementationCheckbox.classList.add("implementationCheckboxContainer");
                        implementationCheckbox.setLocalizedCaption("GUI-740260"); // [IMP]

                        // Nastavení hodnot
                        implementationCheckbox.isChecked = filterBlock.isImplementationFilter;
                        implementationCheckbox.value = filterBlock.isImplementationFilter;

                        implementationCheckbox.action = new Action(async () => {
                            implementationCheckbox.isChecked = !implementationCheckbox.isChecked;
                            implementationCheckbox.value = !implementationCheckbox.value;
                        });

                        item.insertAdjacentElement("afterend", implementationCheckbox);
                    }

                    FilterPanel._promptFilterName({
                        item: item,
                        tempName: filterBlock.filterName,
                        onSuccessAction: async (name, promptItem) => {
                            if (name) {
                                if (filterManager.canEditImplementationFilters) {
                                    filterBlock.isImplementationFilter = implementationCheckbox.value;
                                }
                                await filterBlock.save(name, filterManager);
                                item.setCaption(await filterManager.getFilterDisplayName(filterBlock));
                                item.visible = true;
                                promptItem.visible = false;
                                implementationCheckbox?.remove();
                                promptItem.dispose();
                            } else {
                                promptItem.setInvalid("GUI-625140"); // Musí být vyplněno
                            }
                        },
                        onCancelAction: (promptItem) => {
                            item.visible = true;
                            promptItem.dispose();
                            implementationCheckbox?.remove();
                        },
                    });
                },
                icon: ["edit", "editorial-pencil"],
            });
        buttons.push({
            locKey: "GUI-615974", //Uložit jako
            displayOn: DropDownMenuButtonDisplayOn.hover,
            action: async (item) => {
                item.isEnabled = false;
                item.hideButtons = true;
                FilterPanel._promptFilterName({
                    item: item,
                    tempName: filterBlock.filterName,
                    onSuccessAction: async (name, promptItem) => {
                        if (name) {
                            item.visible = true;
                            const newFilterBlock = await filterBlock.save(name, filterManager, true);
                            const newItem = await filterManager.filterPanel._createNewFilterItem(newFilterBlock.filterId, newFilterBlock);
                            newItem.setCaption(await filterManager.getFilterDisplayName(newFilterBlock));
                            item.parent.addItemAfter(newItem, item);
                            newItem.removeButtons();
                            newItem.addButtons(FilterPanel.getCurrentButtons(newFilterBlock, filterManager));
                            FilterPanel._selectFilter(newFilterBlock, filterManager, true);
                            promptItem.visible = false;
                            item.hideButtons = false;
                            item.isEnabled = true;
                            promptItem.dispose();
                        } else {
                            promptItem.setInvalid("GUI-625140"); // Musí být vyplněno
                        }
                    },
                    onCancelAction: (promptItem) => {
                        item.visible = true;
                        item.hideButtons = false;
                        item.isEnabled = true;
                        promptItem.dispose();
                    },
                    hideCurrentItem: false,
                });
            },
            icon: ["saveas", "tool-floppy-disks"],
        });

        buttons.push(this._getCopyFilterButton(filterBlock, filterManager));
        if (this._canEditFilter(filterBlock, filterManager))
            buttons.push({
                locKey: "GUI-615973", // Smazat filtr
                displayOn: DropDownMenuButtonDisplayOn.hover,
                action: (item) => {
                    item.parent.closeAll();
                    const qm = new MessageWindow(new LocalizedStringBuilder("GUI-599180")); //Odstranit?
                    qm.okButtonCaption = "GUI-146838";
                    qm.noButtonCaption = "GUI-146840";
                    qm.type = MessageType.message;
                    qm.show().then(async (r) => {
                        if (r.result === MessageButtonType.ok) {
                            await filterManager.removeFilter(filterBlock);
                            item.remove();
                        }
                    });
                },
                icon: ["remove", "editorial-trash"],
            });

        return buttons;
    }

    /**
     * Tlačítka uloženého změněného filtru
     * @param filterBlock
     * @param filterManager
     */
    private static _getSaveAsButton(filterBlock: FilterBlock, filterManager: FilterManager): IDropDownMenuButton[] {
        const buttons: IDropDownMenuButton[] = [];
        if (this._canEditFilter(filterBlock, filterManager))
            buttons.push({
                locKey: "GUI-28829", // Uložit filtr
                displayOn: DropDownMenuButtonDisplayOn.hover,
                action: async (item) => {
                    const name = filterBlock.filterName;

                    if (name) {
                        await filterBlock.save(name, filterManager);
                        item.setCaption(await filterManager.getFilterDisplayName(filterBlock));
                    }
                    item.removeButtons();
                    item.addButtons(FilterPanel.getCurrentButtons(filterBlock, filterManager));
                },
                icon: ["save", "tool-floppy-disk"],
            });
        buttons.push({
            locKey: "GUI-615974", // Uložit jako
            displayOn: DropDownMenuButtonDisplayOn.hover,
            action: async (item) => {
                item.isEnabled = false;
                item.hideButtons = true;
                FilterPanel._promptFilterName({
                    item: item,
                    tempName: filterBlock.filterName,
                    onSuccessAction: async (name, promptItem) => {
                        if (name) {
                            item.visible = true;
                            const newFilterBlock = await filterBlock.save(name, filterManager, true);
                            const newItem = await filterManager.filterPanel._createNewFilterItem(newFilterBlock.filterId, newFilterBlock);
                            newItem.setCaption(await filterManager.getFilterDisplayName(newFilterBlock));
                            item.parent.addItemAfter(newItem, item);
                            item.setCaption(await filterManager.getFilterDisplayName(filterBlock));
                            item.removeButtons();
                            item.addButtons(FilterPanel.getCurrentButtons(filterBlock, filterManager));
                            newItem.removeButtons();
                            newItem.addButtons(FilterPanel.getCurrentButtons(newFilterBlock, filterManager));
                            FilterPanel._selectFilter(newFilterBlock, filterManager);
                            promptItem.visible = false;
                            item.hideButtons = false;
                            item.isEnabled = true;
                            promptItem.dispose();
                        } else {
                            promptItem.setInvalid("GUI-625140"); // Musí být vyplněno
                        }
                    },
                    onCancelAction: (promptItem) => {
                        item.visible = true;
                        item.hideButtons = false;
                        item.isEnabled = true;
                        promptItem.dispose();
                    },
                    hideCurrentItem: false,
                });
            },
            icon: ["saveas", "tool-floppy-disks"],
        });

        buttons.push(this._getCopyFilterButton(filterBlock, filterManager));

        buttons.push({
            locKey: "GUI-615976", //Vrátit změny
            displayOn: DropDownMenuButtonDisplayOn.hover,
            action: async (item) => {
                await filterManager.getSavedFilterById(filterBlock.filterId); // Resetuje data filtru

                item.setCaption(await filterManager.getFilterDisplayName(filterBlock));
                item.removeButtons();
                item.addButtons(FilterPanel.getCurrentButtons(filterBlock, filterManager));

                if (filterBlock.isUsed) {
                    filterManager.execute();
                }
            },
            icon: ["Close", "editorial-undo"],
        });
        return buttons;
    }

    /**
     * Tlačítka nového neuloženého filtru
     * @param filterBlock
     * @param filterManager
     */
    private static _getNoSavedButton(filterBlock: FilterBlock, filterManager: FilterManager): IDropDownMenuButton[] {
        const buttons: IDropDownMenuButton[] = [];
        if (this._canEditFilter(filterBlock, filterManager))
            buttons.push({
                locKey: "GUI-28829", // Uložit filtr
                displayOn: DropDownMenuButtonDisplayOn.hover,
                action: async (item) => {
                    FilterPanel._promptFilterName({
                        item: item,
                        tempName: filterBlock.tempName,
                        onSuccessAction: async (name, promptItem) => {
                            if (name) {
                                await filterBlock.save(name, filterManager);
                                item.visible = true;
                                item.setCaption(await filterManager.getFilterDisplayName(filterBlock));
                                item.removeButtons();
                                item.addButtons(FilterPanel.getCurrentButtons(filterBlock, filterManager));
                                promptItem.visible = false;
                                promptItem.dispose();
                            } else {
                                promptItem.setInvalid("GUI-625140"); // Musí být vyplněno
                            }
                        },
                        onCancelAction: (promptItem) => {
                            item.visible = true;
                            promptItem.dispose();
                        },
                    });
                },
                icon: ["save", "tool-floppy-disk"],
            });

        buttons.push(this._getCopyFilterButton(filterBlock, filterManager));

        if (this._canEditFilter(filterBlock, filterManager))
            buttons.push({
                locKey: "GUI-615973", // Smazat filtr
                displayOn: DropDownMenuButtonDisplayOn.hover,
                action: (item) => {
                    item.parent.closeAll();
                    const qm = new MessageWindow(new LocalizedStringBuilder("GUI-599180"));
                    qm.okButtonCaption = "GUI-146838";
                    qm.noButtonCaption = "GUI-146840";
                    qm.type = MessageType.message;
                    qm.show().then(async (r) => {
                        if (r.result === MessageButtonType.ok) {
                            await filterManager.removeFilter(filterBlock);
                            item.remove();
                        }
                    });
                },
                icon: ["remove", "editorial-trash"],
            });

        return buttons;
    }

    private static _getCopyFilterButton(filterBlock: FilterBlock, _filterManager: FilterManager): IDropDownMenuButton {
        return {
            locKey: "GUI-673291", // Zkopírovat definici
            displayOn: DropDownMenuButtonDisplayOn.hover,
            action: (item) => {
                item.parent.closeAll();
                UIUtils.pasteToClipboard(JSON.stringify(filterBlock.getFilterData(filterBlock.type, true)));
            },
            icon: ["copy"],
        };
    }

    public static getCurrentButtons(filterBlock: FilterBlock, filterManager: FilterManager): IDropDownMenuButton[] {
        if (filterBlock.isSaved) {
            return FilterPanel._getSavedButton(filterBlock, filterManager);
        } else {
            if (filterBlock.filterId) { // pokud je existující, ale rozeditovany
                return FilterPanel._getSaveAsButton(filterBlock, filterManager);
            } else { // pokud je neexistující
                return FilterPanel._getNoSavedButton(filterBlock, filterManager);
            }
        }
    }
    //#endregion Tvorba buttonu

    /**
     * Vytvoří a přidá za/místo vybranou položku filtru box pro zadání jména filtru
     */
    private static _promptFilterName({ item, tempName, onSuccessAction, onCancelAction, hideCurrentItem = true }: IPromptFilterName): void {
        const newItem = new DropDownPromptItem();
        newItem.classList.add("prompt-nameEditing");

        newItem.text = tempName;
        newItem.okAction = onSuccessAction;
        newItem.cancelAction = onCancelAction;
        newItem.parent = item.parent;
        item.insertAdjacentElement("afterend", newItem);
        newItem.focusEdit();

        if (hideCurrentItem) {
            item.visible = false; // skrýt původní element
        }
    }

    /**
     * Vybere požadovaný blok filtrů
     * @param filterBlock Blok filtrů
     * @param filterManager Manažer aktuálního framu
     * @param updatePanel Zda má být aktualizován panel s filtry
     */
    private static _selectFilter(filterBlock: FilterBlock, filterManager: FilterManager, updatePanel: boolean = true): void {
        filterManager.selectFilter(filterBlock);

        if (updatePanel) {
            filterManager.updateFilterPanel();
        }

        filterManager.execute();
    }

    /**
     * Vytvoření nové položky menu pro filtr do dropdownu
     */
    private async _createNewFilterItem(_id: string, filterBlock: FilterBlock): Promise<DropDownMenuItemWithButton> {
        const buttons: IDropDownMenuButton[] = FilterPanel.getCurrentButtons(filterBlock, this._filterManager);

        const menuItem = FilterPanel.createDropMenuItemForFilters("None", async () => {
            // při kliku na položku menu dojde k vybrání, zobrazení a spuštění filtru
            FilterPanel._selectFilter(filterBlock, this._filterManager);
        }, undefined, true, buttons);

        menuItem.setCaption(await this._filterManager.getFilterCaption(filterBlock));

        if (filterBlock.isUsed) {
            menuItem.icon = "checked";
            this._filterMenuEmptyBtn.icon = "none";
        } else
            menuItem.icon = "none";

        filterBlock.registerEventListener("filterSelected", (e: IBooleanDetailValueEvent) => {
            if (e.detail.value) {
                menuItem.icon = "checked";
                this._filterMenuEmptyBtn.icon = "none";
            } else
                menuItem.icon = "none";
        });

        return menuItem;
    }

    private static _createDropMenuItem(icon: string, fnc: () => Promise<void>, locKey: string, enabled: boolean = true): DropDownMenuItem {
        const element = new DropDownMenuItem();
        const action = new Action(fnc, locKey);

        element.action = action;
        action.enabled = enabled;

        element.icon = icon;

        return element;
    }

    private async _createHeaderRowButtons(): Promise<void> {
        const createButtons = async (buttonsContainer: HTMLDivElement) => {
            this._headerTurnOffButton = await this._createHeaderTurnOffButton();
            FilterPanel._appendDisabledButton(buttonsContainer, this._headerTurnOffButton);

            this._headerSaveAsButton = await this._createHeaderSaveAsButton();
            FilterPanel._appendDisabledButton(buttonsContainer, this._headerSaveAsButton);

            this._headerSaveButton = await this._createHeaderSaveButton();
            FilterPanel._appendDisabledButton(buttonsContainer, this._headerSaveButton);

            this._headerRevertChangesButton = await this._createHeaderRevertChangesButton();
            FilterPanel._appendDisabledButton(buttonsContainer, this._headerRevertChangesButton);

            this._headerDeleteButton = await this._createHeaderDeleteButton();
            FilterPanel._appendDisabledButton(buttonsContainer, this._headerDeleteButton);

            return true;
        };

        if (!this._headerRowButtonsInitiated) {
            this._headerRowButtonsInitiated = true;
            this._createHeaderRowButtonsPromise = createButtons(this._headerRowActionButtons);
            await this._createHeaderRowButtonsPromise;
            this._createHeaderRowButtonsPromise = null;

            this._headerRowButtonsCreated = true;
            this._headerPanel.appendChild(this._headerRowActionButtons);
        }
    }

    private static _appendDisabledButton(buttonsContainer: HTMLDivElement, button: HTMLButtonElement): void {
        button.disabled = true;
        buttonsContainer.appendChild(button);
    }

    private async _updateStateOfButtons(): Promise<void> {
        const currentFilterBlock = this._filterManager.getCurrentFilter();

        if (!currentFilterBlock) {
            if (this._headerRowButtonsCreated || (this._createHeaderRowButtonsPromise && await this._createHeaderRowButtonsPromise)) {
                this._headerSaveAsButton.disabled = true;
                this._headerSaveButton.disabled = true;
                this._headerRevertChangesButton.disabled = true;
                this._headerTurnOffButton.disabled = true;
                this._headerDeleteButton.disabled = true;
            }
            return;
        }

        // Informace, že jde o implementační filtr a uživatel nemá právo ho měnit
        const impDisabled = !FilterPanel._canEditFilter(currentFilterBlock, this._filterManager);
        this._headerSaveAsButton.disabled = !(currentFilterBlock.isSaved || currentFilterBlock.filterId);

        if (!currentFilterBlock.isSaved) {
            this._headerSaveButton.disabled = impDisabled;

            if (currentFilterBlock.filterId) {
                this._headerRevertChangesButton.disabled = false;
            }
        } else {
            this._headerSaveButton.disabled = true;
            this._headerRevertChangesButton.disabled = true;
        }

        if (currentFilterBlock.isVisible()) {
            this._headerTurnOffButton.disabled = false;
            this._headerDeleteButton.disabled = impDisabled;
        }
    }

    private async _createHeaderTurnOffButton(): Promise<HTMLButtonElement> {
        const headerTurnOffButton = await this._createLocalisedFlatButton("GUI-565552"); //Vypnout

        this._events.addListener(headerTurnOffButton, "click", async () => {
            if (!(await this.ownerForm.checkAndSaveForm({ cancelEditIfNotEdited: true }))) {
                return;
            }

            this._filterMenuEmptyBtn.icon = "checked";
            await this._resetHeaderName();
            this._filterManager.clearFilter();
        });

        return headerTurnOffButton;
    }

    private async _createHeaderRevertChangesButton(): Promise<HTMLButtonElement> {
        const revertChangesButton = await this._createLocalisedFlatButton("GUI-615976"); // Zrušit

        this._events.addListener(revertChangesButton, "click", async () => {
            const filterBlock = this._filterManager.getCurrentFilter();
            await this._filterManager.getSavedFilterById(filterBlock.filterId);

            if (filterBlock.isUsed) {
                this._filterManager.execute();
            }
            await this._updateStateOfButtons();
        });

        return revertChangesButton;
    }

    private async _createHeaderDeleteButton(): Promise<HTMLButtonElement> {
        const headerDeleteButton = await this._createLocalisedFlatButton("GUI-456318"); // Smazat

        this._events.addListener(headerDeleteButton, "click", async () => {
            const filterBlock = this._filterManager.getCurrentFilter();
            const messageWindow = new MessageWindow(new LocalizedStringBuilder("GUI-599180")); // Odstranit
            messageWindow.okButtonCaption = "GUI-146838"; // Ano
            messageWindow.noButtonCaption = "GUI-146840"; // Ne
            messageWindow.type = MessageType.message;
            messageWindow.show().then(async (response) => {
                if (response.result === MessageButtonType.ok) {
                    await this._resetPanel();
                    await this._filterManager.removeFilter(filterBlock);
                    await this._resetHeaderName();
                }
            });
        });

        return headerDeleteButton;
    }

    private async _createHeaderSaveButton(): Promise<HTMLButtonElement> {
        const headerSaveButton = await this._createLocalisedFlatButton("GUI-292626"); // Uložit

        this._events.addListener(headerSaveButton, "click", async () => {
            const filterBlock = this._filterManager.getCurrentFilter();
            await filterBlock.save((filterBlock.filterName ?? this._headerText.innerText), this._filterManager);
        });

        return headerSaveButton;
    }

    private async _createHeaderSaveAsButton(): Promise<HTMLButtonElement> {
        const headerSaveAsButton = await this._createLocalisedFlatButton("GUI-583316"); // Uložit jako

        this._events.addListener(headerSaveAsButton, "click", async () => {
            this._promptHeaderEditing(true);
        });

        return headerSaveAsButton;
    }

    private _createLocalisedFlatButton = async (lockey: string): Promise<HTMLButtonElement> => {
        const button = document.createElement("button");
        button.textContent = await Localization.getTextPromise(lockey);
        button.classList.add("btn", "flat");
        return button;
    };

    private async _resetPanel(): Promise<void> {
        this._filterMenuEmptyBtn.icon = "checked";
        await this._updateStateOfButtons();

        if (this._headerImplementationCheckbox) {
            this._headerImplementationCheckbox.visible = false;
        }
    }

    private async _resetHeaderName(): Promise<void> {
        this._headerText.innerText = await Localization.getTextPromise("GUI-289646"); // Filtr
    }

    private _changeHeaderFilterName(name: string): void {
        if (!name) {
            return;
        }

        this._headerText.innerText = name;
    }

    private _removeNameEditingIcons(): void {
        this._nameEditingInput?.remove();
        this._nameEditingIconsRow?.remove();
    }

    private _cancelNameEditing(): void {
        this._isNameEditing = false;

        this._headerDropdownIcon.style.visibility = "visible";

        if (this._headerImplementationCheckbox) {
            this._headerImplementationCheckbox.visible = true;
        }

        this._headerPanel.classList.remove("nameEditing");
        this._headerRowActionButtons.style.display = "flex";
        this._headerText.parentElement.style.display = "flex";

        this._headerText.hidden = false;

        this._removeNameEditingIcons();
    }

    private _promptHeaderEditing(saveAs?: boolean): void {
        const filterBlock = this._filterManager.getCurrentFilter();

        if (filterBlock && !this._isNameEditing && (FilterPanel._canEditFilter(filterBlock, this._filterManager) || saveAs)) {
            this._isNameEditing = true;
            this._headerPanel.classList.add("nameEditing");
            const filterName = this._filterManager.getCurrentFilter().filterName || this._headerText.innerText;

            this._nameEditingInput = createElement({ type: "input", value: filterName, attrs: { "minlength": "1", "type": "text" } }) as HTMLInputElement;
            this._nameEditingIconsRow = createElement({ type: "div", classes: ["nameEditingIconsRow"] }) as HTMLDivElement;

            const nameEditingSaveIconButton = createElement({ type: "button", classes: ["btn", "flat", "icon", "tool-floppy-disk"] }) as HTMLButtonElement;
            const nameEditingCancelIconButton = createElement({ type: "button", classes: ["btn", "flat", "icon", "ui-undo-a"] }) as HTMLButtonElement;

            this._nameEditingIconsRow.appendChild(nameEditingSaveIconButton);
            this._nameEditingIconsRow.appendChild(nameEditingCancelIconButton);

            this._headerPanel.prepend(this._nameEditingIconsRow);
            this._headerPanel.prepend(this._nameEditingInput);

            this._headerText.parentElement.style.display = "none";
            this._headerRowActionButtons.style.display = "none";
            this._headerDropdownIcon.style.visibility = "hidden";

            if (this._headerImplementationCheckbox) {
                this._headerImplementationCheckbox.visible = false;
            }

            this._events.addListener(nameEditingSaveIconButton, "click", async () => {
                if (this._nameEditingInput?.value?.trimLeft().length > 0) {
                    if (saveAs) {
                        if (await this._filterManager.doesFilterNameExist(this._nameEditingInput.value)) {
                            MessageWindow.showAlert(new LocalizedStringBuilder("GUI-742707")); // Nelze použít název, který odpovídá názvu existujícího filtru.
                            return;
                        }
                        const newFilterBlock = await filterBlock.save(this._nameEditingInput.value, this._filterManager, true);
                        const newItem = await this._filterManager.filterPanel._createNewFilterItem(newFilterBlock.filterId, newFilterBlock);
                        newItem.setCaption(newFilterBlock.filterName);
                        this._changeHeaderFilterName(newFilterBlock.filterName);

                        FilterPanel._selectFilter(newFilterBlock, this._filterManager, true);
                    } else {
                        await filterBlock.save(this._nameEditingInput.value, this._filterManager);
                        this._changeHeaderFilterName(this._nameEditingInput.value);
                    }

                    this._headerText.parentElement.style.display = "flex";
                    this._headerDropdownIcon.style.visibility = "visible";

                    if (this._headerImplementationCheckbox) {
                        this._headerImplementationCheckbox.visible = true;
                    }

                    this._isNameEditing = false;
                    this._cancelNameEditing();
                    await this._updateStateOfButtons();
                } else {
                    this._nameEditingInput.classList.add("invalid");
                }
            });

            this._events.addListener(nameEditingCancelIconButton, "click", async () => {
                this._headerText.parentElement.style.display = "flex";

                this._cancelNameEditing();
                await this._updateStateOfButtons();

                if (filterBlock.isUsed) {
                    this._filterManager.execute();
                }
            });
        }
    }

    public static createDropMenuItemForFilters(icon: string, fnc: () => Promise<void>, locKey: string, enabled: boolean = true, buttons: IDropDownMenuButton[] = []): DropDownMenuItemWithButton {
        const element = new DropDownMenuItemWithButton();
        element.floatMenu = true;
        const action = new Action(fnc, locKey);

        for (const button of buttons) {
            element.addButton(button);
        }

        element.action = action;
        action.enabled = enabled;

        element.icon = icon;

        return element;
    }

    /**
     * skryje panel filtru
     */
    public hide(): void {
        this.classList.remove("visible");
        this.setAttribute("aria-hidden", "true");
        this._filterButton.tabIndex = -1;
    }

    /**
     * zobrazí panel filtru
     */
    public show(): void {
        this.classList.add("visible");
        this.removeAttribute("aria-hidden");
        this._filterButton.tabIndex = 0;
    }

    public isShown(frameName: string = null): boolean {
        if (frameName && this._filterManager && frameName !== this._filterManager.frameName)
            return false;

        return this.classList.contains("visible");
    }

    public override dispose(): void {
        this._events.dispose();
        this._filterMenu?.dispose();
        this._filterCombo?.dispose();
        super.dispose();
    }
    //#endregion Methods
}

interface IPromptFilterName {
    /**
     * Vybraná položka menu filtrů
     */
    readonly item: DropDownMenuItemWithButton;
    /**
     * Jméno položky
     */
    readonly tempName: string;
    /**
     * Akce při potvrzení názvu
     */
    readonly onSuccessAction: (name: string, promptItem: DropDownPromptItem) => Promise<void>;
    /**
     * Akce při zrušení zadávání názvu
     */
    readonly onCancelAction: (promptItem: DropDownPromptItem) => void;
    /**
     * Příznak, zda se má zadávací box zobrazit místo vybrané položky
     */
    readonly hideCurrentItem?: boolean;
}

registerCustomElement("tsc-filterpanel", FilterPanel);
